import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setPaymentStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('paid');
          clearCart();
          
          // Fetch order details
          if (orderId) {
            const orderResponse = await axios.get(`${API}/orders/${orderId}`);
            setOrder(orderResponse.data);
          }
        } else if (response.data.status === 'expired') {
          setPaymentStatus('failed');
        } else if (pollCount < 5) {
          // Continue polling
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setPaymentStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setPaymentStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, orderId, pollCount, clearCart]);

  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#00ff9d]" />
          <p className="mt-4 text-zinc-600">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'failed' || paymentStatus === 'error') {
    return (
      <div className="min-h-screen bg-white py-24">
        <div className="max-w-md mx-auto px-6 text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
            <span className="text-red-500 text-3xl">×</span>
          </div>
          <h1 className="font-heading font-black text-2xl uppercase mb-4">
            Payment Failed
          </h1>
          <p className="text-zinc-500 mb-8">
            Something went wrong with your payment. Please try again.
          </p>
          <Link to="/cart" className="btn-primary inline-block">
            Return to Cart
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Success Header */}
      <div className="bg-[#00ff9d] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.6 }}
          >
            <CheckCircle className="w-20 h-20 mx-auto text-black mb-6" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h1 
              className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight text-black"
              data-testid="thank-you-title"
            >
              Thanks for Your Order!
            </h1>
            <p className="text-black/70 mt-4 text-lg">
              {order?.deliveryMethod === 'pickup' 
                ? 'Your G.O.A.T. gear is ready for pickup.' 
                : 'Your G.O.A.T. gear is on the way.'}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Order Details */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {order && (
            <div className="bg-zinc-50 p-6 md:p-8 mb-8" data-testid="order-details">
              <div className="flex items-center gap-3 mb-6">
                <Package className="w-6 h-6 text-black" />
                <h2 className="font-heading font-bold text-xl uppercase tracking-tight">
                  Order Summary
                </h2>
              </div>

              <div className="space-y-2 text-sm mb-6">
                <p><span className="text-zinc-500">Order ID:</span> <span className="font-medium">{order.id}</span></p>
                <p><span className="text-zinc-500">Email:</span> <span className="font-medium">{order.customerEmail}</span></p>
              </div>

              {/* Items */}
              <div className="border-t border-zinc-200 pt-6 space-y-4">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{item.productName}</p>
                      <p className="text-zinc-500 text-sm">
                        {item.size && `Size: ${item.size}`}
                        {item.size && item.colour && ' / '}
                        {item.colour && `Colour: ${item.colour}`}
                        {' × '}{item.quantity}
                      </p>
                    </div>
                    <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-zinc-200 mt-6 pt-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Subtotal</span>
                  <span>${order.subtotal?.toFixed(2)}</span>
                </div>
                {order.appliedDiscountCode && order.discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-[#00cc7d]">
                    <span>Discount ({order.appliedDiscountCode})</span>
                    <span>-${order.discountAmount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">
                    {order.deliveryMethod === 'pickup' ? 'Shipping (Local Pickup)' : 'Shipping'}
                  </span>
                  <span className={order.shippingCost === 0 ? 'text-[#00cc7d] font-medium' : ''}>
                    {order.shippingCost === 0 ? 'FREE' : `$${order.shippingCost?.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2">
                  <span>Total</span>
                  <span>${order.totalAmount?.toFixed(2)} AUD</span>
                </div>
              </div>

              {/* Delivery Method */}
              <div className="border-t border-zinc-200 mt-6 pt-6">
                {order.deliveryMethod === 'pickup' ? (
                  <div className="bg-amber-50 border border-amber-200 p-4">
                    <p className="font-bold text-amber-800">🏪 Local Pickup</p>
                    <p className="text-amber-700 text-sm mt-2">
                      Pickup will be arranged separately. We'll contact you at {order.customerEmail} with pickup details.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-zinc-500 text-sm mb-2">📦 Shipping to:</p>
                    <p className="font-medium">
                      {order.customerFirstName} {order.customerLastName}
                    </p>
                    <p className="text-zinc-600 text-sm">
                      {order.shippingStreet}<br />
                      {order.shippingSuburb}, {order.shippingState} {order.shippingPostcode}<br />
                      {order.shippingCountry}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <Link 
              to="/shop" 
              className="btn-primary inline-flex items-center gap-2 group"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
